import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  getMonthShortName,
  getWeekdayShortName,
} from '@shared/utils/date-format.utils';

/**
 * Replacement for ng-bootstrap's built-in `NgbDatepickerI18nDefault`.
 *
 * The stock provider is declared as
 * `{ provide: NgbDatepickerI18n, useFactory: () => new NgbDatepickerI18nDefault() }`
 * and `NgbDatepickerI18nDefault` resolves `LOCALE_ID` via a field-initializer
 * `inject()` during that `new`. When the datepicker popup is created lazily
 * (inside the calendar button's click handler) Angular 21's stricter DI cycle
 * detection trips on that self-referential factory and throws
 * `NG0200: Circular dependency detected for 'NgbDatepickerI18n'`, so the popup
 * never opens. Worked on ng-bootstrap 13, broke on 20 + Angular 21.
 *
 * This implementation produces identical (Intl-based) output but takes
 * `LOCALE_ID` through the constructor, so it is built through normal DI in a
 * proper injection context — no self-`new` factory, no circular dependency.
 */
@Injectable()
export class AppDatepickerI18n extends NgbDatepickerI18n {
  private readonly locale: string;
  private readonly monthsShort: string[];
  private readonly monthsFull: string[];

  constructor(@Inject(LOCALE_ID) locale: string) {
    super();
    // The app stores locales as `en_US`/`de_CH`/`fr_CH` (underscore), but
    // `Intl` requires a BCP 47 tag with a hyphen (`en-US`) and throws
    // `RangeError: Invalid language tag` otherwise. Normalize, and fall back to
    // a known-good tag if anything is still unrecognized.
    this.locale = AppDatepickerI18n.toBcp47(locale);
    this.monthsShort = [...new Array(12).keys()].map(month =>
      getMonthShortName(month, this.locale)
    );
    this.monthsFull = [...new Array(12).keys()].map(month =>
      new Intl.DateTimeFormat(this.locale, {
        month: 'long',
        timeZone: 'UTC',
      }).format(Date.UTC(2000, month))
    );
  }

  getWeekdayLabel(
    weekday: number,
    width: Exclude<Intl.DateTimeFormatOptions['weekday'], undefined> = 'narrow'
  ): string {
    if (width === 'short') {
      // ng-bootstrap weekdays run 1 (Monday) to 7 (Sunday)
      return getWeekdayShortName(weekday % 7, this.locale);
    }

    // 1 May 2000 is a Monday
    const weekdays = [1, 2, 3, 4, 5, 6, 7].map(day =>
      new Intl.DateTimeFormat(this.locale, {
        weekday: width,
        timeZone: 'UTC',
      }).format(Date.UTC(2000, 4, day))
    );
    return weekdays[weekday - 1] || '';
  }

  getMonthShortName(month: number): string {
    return this.monthsShort[month - 1] || '';
  }

  getMonthFullName(month: number): string {
    return this.monthsFull[month - 1] || '';
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(jsDate);
  }

  private static toBcp47(locale: string): string {
    const normalized = (locale || 'en').replaceAll('_', '-');
    try {
      // Throws RangeError for an invalid tag; if so, fall back to English.
      Intl.getCanonicalLocales(normalized);
      return normalized;
    } catch {
      return 'en';
    }
  }
}
